import type { StorefrontBrand, StorefrontCatalog } from '@erp/shared';
import { ChevronLeft, ChevronRight, Search, SlidersHorizontal, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { formatMoney } from '../../lib/money.js';
import { assetUrl, publicApi } from '../../lib/publicApi.js';

import { pricingRecorder } from './buyerPricing.js';
import { GroupPricingBadge } from './GroupPricingBadge.js';
import { PriceRangeSlider } from './PriceRangeSlider.js';
import { ProductCard } from './ProductCard.js';
import { ProductGridSkeleton } from './StorefrontSkeletons.js';

// Page size: a full 48-card page beats paging through 20s, and 48 divides evenly
// by the grid's 2 / 3 / 4 columns so the last row is never a ragged orphan.
const PAGE = 48;
type Sort = 'featured' | 'best_selling' | 'most_viewed' | 'newest' | 'price_asc' | 'price_desc' | 'name';
const SORTS: readonly Sort[] = ['featured', 'best_selling', 'most_viewed', 'newest', 'price_asc', 'price_desc', 'name'];
const asSort = (v: string | null): Sort => (v && (SORTS as readonly string[]).includes(v) ? (v as Sort) : 'featured');

export function StorefrontCatalogPage(): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { slug, site } = useStorefront();
  const [searchParams] = useSearchParams();
  const urlQ = searchParams.get('q') ?? '';
  const urlSort = searchParams.get('sort');
  const urlOnSale = searchParams.get('onSale') === 'true';
  const [q, setQ] = useState(urlQ);
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [sort, setSort] = useState<Sort>(asSort(urlSort));
  const [inStock, setInStock] = useState(false);
  // Only the server can decide what is discounted (a rule resolves against THIS
  // shopper's own price), so this is a pass-through flag, not a local predicate.
  const [onSale, setOnSale] = useState(urlOnSale);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [offset, setOffset] = useState(0);
  const [brands, setBrands] = useState<StorefrontBrand[]>([]);
  const [data, setData] = useState<StorefrontCatalog | null>(null);
  // Price-slider bounds — sticky across price changes (the API returns the range
  // of the matching set ignoring the price filter), rescaling only on category/brand/q.
  const [bounds, setBounds] = useState<{ min: number; max: number } | null>(null);
  // Currency the last response was priced in — a change means the shopper's
  // pricing basis moved (signed in/out of a customer group).
  const lastCurrency = useRef<string | null>(null);

  // Reset to page 1 whenever a filter changes.
  const onFilter = <T,>(setter: (v: T) => void) => (v: T): void => { setter(v); setOffset(0); };

  useEffect(() => {
    void publicApi().get<{ items: StorefrontBrand[] }>(`/${slug}/brands`).then((r) => setBrands(r.data.items)).catch(() => setBrands([]));
  }, [slug]);

  // Apply a query arriving via the URL (?q=) — e.g. from the header search.
  useEffect(() => { setQ(urlQ); setOffset(0); }, [urlQ]);
  // Apply a sort arriving via the URL (?sort=) — e.g. Home "Best sellers → View all".
  useEffect(() => { if (urlSort) { setSort(asSort(urlSort)); setOffset(0); } }, [urlSort]);
  // ...and a deals arrival (?onSale=true) — Home "Deals → View all".
  useEffect(() => { if (urlOnSale) { setOnSale(true); setOffset(0); } }, [urlOnSale]);

  useEffect(() => {
    const params: Record<string, string | number> = { limit: PAGE, offset };
    if (q.trim()) params.q = q.trim();
    if (category) params.category = category;
    if (brand) params.brand = brand;
    if (sort !== 'featured') params.sort = sort;
    if (inStock) params.inStock = 'true';
    if (onSale) params.onSale = 'true';
    if (minPrice.trim() && Number(minPrice) >= 0) params.minPrice = minPrice.trim();
    if (maxPrice.trim() && Number(maxPrice) >= 0) params.maxPrice = maxPrice.trim();
    setData(null);
    // The response says which group + currency it was priced for — cache it so
    // the cart/checkout key off the shopper's currency, not the store's.
    const remember = pricingRecorder(slug);
    void publicApi().get<StorefrontCatalog>(`/${slug}/products`, { params }).then((r) => {
      setData(r.data);
      remember(r.data.pricing);
      // Signing in/out re-denominates every price: a min/max typed in the old
      // currency would filter the new one nonsensically, so drop it (refetches).
      const ccy = r.data.pricing?.currencyCode ?? site.currencyCode;
      if (lastCurrency.current !== ccy) {
        lastCurrency.current = ccy;
        if (minPrice || maxPrice) { setMinPrice(''); setMaxPrice(''); setOffset(0); }
      }
      if (r.data.priceBounds && r.data.priceBounds.max > r.data.priceBounds.min) setBounds(r.data.priceBounds);
    }).catch(() => setData({ items: [], total: 0, categories: [] }));
  }, [slug, q, category, brand, sort, inStock, onSale, minPrice, maxPrice, offset, i18n.language, site.currencyCode]);

  // Bounds/filters are in the SAME currency the server priced this response in
  // (a customer group's own currency, else the store's) — never the store-wide one.
  const currency = data?.pricing?.currencyCode ?? site.currencyCode;
  const money = (v: number): string => formatMoney(String(v), { locale: i18n.language, currency });

  const control = 'h-10 max-w-[13rem] rounded-full border border-border bg-bg-subtle px-4 text-sm outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20';

  const filtersActive = !!brand || sort !== 'featured' || inStock || onSale || !!minPrice || !!maxPrice || !!category || !!q;
  const clearAll = (): void => { setQ(''); setCategory(''); setBrand(''); setSort('featured'); setInStock(false); setOnSale(false); setMinPrice(''); setMaxPrice(''); setOffset(0); };

  return (
    <div className="flex flex-col gap-6">
      <Helmet>
        <title>{`${t('store.shop.title')} · ${site.storeName}`}</title>
        {site.tagline && <meta name="description" content={site.tagline} />}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${t('store.shop.title')} · ${site.storeName}`} />
        {site.tagline && <meta property="og:description" content={site.tagline} />}
        {assetUrl(site.logoUrl) && <meta property="og:image" content={assetUrl(site.logoUrl)!} />}
      </Helmet>

      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight">{t('store.shop.title')}</h1>
            <GroupPricingBadge />
          </div>
          {site.tagline && <p className="mt-1 text-sm text-fg-muted">{site.tagline}</p>}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-muted" aria-hidden />
          <input
            value={q}
            onChange={(e) => onFilter(setQ)(e.target.value)}
            placeholder={t('store.shop.search')}
            className="h-11 w-full rounded-full border border-border bg-bg-subtle ps-9 pe-4 text-sm outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20"
            aria-label={t('store.shop.search')}
          />
        </div>
      </header>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-bg-subtle/50 p-3">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-fg-muted"><SlidersHorizontal className="h-4 w-4" aria-hidden />{t('store.shop.filters', { defaultValue: 'Filters' })}</span>

        <select value={sort} onChange={(e) => onFilter(setSort)(e.target.value as Sort)} className={control} aria-label={t('store.shop.sort', { defaultValue: 'Sort' })}>
          <option value="featured">{t('store.shop.sortFeatured', { defaultValue: 'Featured' })}</option>
          <option value="best_selling">{t('store.shop.sortBestSelling', { defaultValue: 'Best selling' })}</option>
          <option value="most_viewed">{t('store.shop.sortMostViewed', { defaultValue: 'Most viewed' })}</option>
          <option value="newest">{t('store.shop.sortNewest', { defaultValue: 'Newest' })}</option>
          <option value="price_asc">{t('store.shop.sortPriceAsc', { defaultValue: 'Price: low to high' })}</option>
          <option value="price_desc">{t('store.shop.sortPriceDesc', { defaultValue: 'Price: high to low' })}</option>
          <option value="name">{t('store.shop.sortName', { defaultValue: 'Name (A–Z)' })}</option>
        </select>

        {/* Categories collapse into a dropdown — stores can have dozens, and a
            chip cloud for all of them swallowed the whole first screen. */}
        {(data?.categories.length ?? 0) > 0 && (
          <select value={category} onChange={(e) => onFilter(setCategory)(e.target.value)} className={control} aria-label={t('store.category.shopBy', { defaultValue: 'Shop by category' })}>
            <option value="">{t('store.shop.allCategories', { defaultValue: 'All categories' })}</option>
            {data!.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}

        {brands.length > 0 && (
          <select value={brand} onChange={(e) => onFilter(setBrand)(e.target.value)} className={control} aria-label={t('store.brands.title', { defaultValue: 'Brands' })}>
            <option value="">{t('store.shop.allBrands', { defaultValue: 'All brands' })}</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}

        {bounds && bounds.max > bounds.min && (() => {
          const lo = minPrice !== '' ? Math.max(bounds.min, Math.min(Number(minPrice), bounds.max)) : bounds.min;
          const hi = maxPrice !== '' ? Math.min(bounds.max, Math.max(Number(maxPrice), bounds.min)) : bounds.max;
          return (
            // basis-full: on a phone the price block claims its own line in the
            // wrapping filter bar (a shared line squeezes the two custom fields).
            <div className="flex w-full min-w-0 basis-full flex-col gap-1.5 rounded-2xl border border-border bg-bg px-4 py-2 sm:w-auto sm:min-w-[20rem] sm:basis-auto sm:grow sm:flex-row sm:items-center sm:gap-3">
              <span className="whitespace-nowrap text-sm font-medium text-fg-muted">{t('store.shop.price', { defaultValue: 'Price' })}</span>
              <PriceRangeSlider
                min={bounds.min}
                max={bounds.max}
                lo={lo}
                hi={hi}
                format={money}
                currency={currency}
                fromLabel={t('store.shop.priceFrom', { defaultValue: 'From' })}
                toLabel={t('store.shop.priceTo', { defaultValue: 'To' })}
                onCommit={(l, h) => {
                  setMinPrice(l <= bounds.min ? '' : String(l));
                  setMaxPrice(h >= bounds.max ? '' : String(h));
                  setOffset(0);
                }}
              />
            </div>
          );
        })()}

        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border bg-bg px-3 py-1.5 text-sm">
          <input type="checkbox" checked={inStock} onChange={(e) => onFilter(setInStock)(e.target.checked)} className="h-4 w-4 accent-[var(--color-brand)]" />
          {t('store.shop.inStock', { defaultValue: 'In stock only' })}
        </label>

        {/* Tinted like the Deals band on the home page, so the filter and the
            section it came from read as the same thing. */}
        <label className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition ${onSale ? 'border-danger/40 bg-danger/10 text-danger' : 'border-border bg-bg'}`}>
          <input type="checkbox" checked={onSale} onChange={(e) => onFilter(setOnSale)(e.target.checked)} className="h-4 w-4 accent-[var(--color-danger)]" />
          {t('store.shop.onSale', { defaultValue: 'On sale' })}
        </label>

        {filtersActive && (
          <button type="button" onClick={clearAll} className="inline-flex items-center gap-1 text-sm font-medium text-fg-muted transition hover:text-danger">
            <X className="h-3.5 w-3.5" aria-hidden />{t('store.shop.clear', { defaultValue: 'Clear' })}
          </button>
        )}
      </div>

      {!data ? (
        <ProductGridSkeleton count={PAGE} />
      ) : data.items.length === 0 ? (
        <div className="py-16 text-center text-fg-muted">{t('store.shop.empty')}</div>
      ) : (
        <>
          <div className="text-sm text-fg-muted">{t('store.shop.results', { defaultValue: '{{count}} products', count: data.total })}</div>
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
            {data.items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
          {data.total > PAGE && (() => {
            const pageCount = Math.ceil(data.total / PAGE);
            const current = Math.floor(offset / PAGE);
            const go = (p: number): void => setOffset(Math.min(Math.max(p, 0), pageCount - 1) * PAGE);
            const nums: Array<number | 'gap'> = [];
            for (let p = 0; p < pageCount; p++) {
              if (pageCount <= 7 || p === 0 || p === pageCount - 1 || Math.abs(p - current) <= 1) nums.push(p);
              else if (nums[nums.length - 1] !== 'gap') nums.push('gap');
            }
            const pill = (active: boolean): string =>
              `grid h-9 min-w-9 place-items-center rounded-full px-3 text-sm font-medium transition disabled:opacity-40 ${active ? 'bg-brand text-brand-contrast shadow-sm' : 'border border-border text-fg-muted hover:border-brand/40 hover:text-fg'}`;
            return (
              <nav className="flex items-center justify-center gap-2" aria-label={t('store.shop.pagination')}>
                <button type="button" onClick={() => go(current - 1)} disabled={current === 0} className={pill(false)} aria-label={t('store.shop.prev')}>
                  <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
                </button>
                {nums.map((n, i) => n === 'gap'
                  ? <span key={`gap-${i}`} className="px-1 text-fg-muted" aria-hidden>…</span>
                  : <button key={n} type="button" onClick={() => go(n)} aria-current={n === current ? 'page' : undefined} className={pill(n === current)}>{n + 1}</button>)}
                <button type="button" onClick={() => go(current + 1)} disabled={current >= pageCount - 1} className={pill(false)} aria-label={t('store.shop.next')}>
                  <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
                </button>
              </nav>
            );
          })()}
        </>
      )}
    </div>
  );
}
